import { useCallback, useMemo } from 'react';
import {
  getCommentsForField,
  getCommentCountForField,
  organizeCommentsByField,
} from '../../../components/common/Comments/commentUtils';

/**
 * useFieldComments Hook
 *
 * Provides field-level comment organization and access.
 * Organizes comments by formId_subformId_fieldId key and provides
 * helper functions to get comments for specific fields.
 *
 * @param {Array} comments - Array of all comments
 * @returns {Object} - Field comment utilities
 */
const useFieldComments = (comments = []) => {
  // Organize comments by field on mount and when comments change
  const commentsByField = useMemo(
    () => organizeCommentsByField(comments),
    [comments]
  );

  // Get comments for a specific field
  const getFieldComments = useCallback(
    (formId, subformId, fieldId) => {
      return getCommentsForField(comments, formId, subformId, fieldId);
    },
    [comments]
  );

  // Get comment count for a specific field
  const getFieldCommentCount = useCallback(
    (formId, subformId, fieldId) => {
      return getCommentCountForField(comments, formId, subformId, fieldId);
    },
    [comments]
  );

  // Check if a field has comments
  const hasFieldComments = useCallback(
    (formId, subformId, fieldId) => {
      return getFieldCommentCount(formId, subformId, fieldId) > 0;
    },
    [getFieldCommentCount]
  );

  return {
    commentsByField,
    getFieldComments,
    getFieldCommentCount,
    hasFieldComments,
  };
};

export default useFieldComments;
